import cx from 'classnames';
import * as React from 'react';
import { trackEvent } from '../analytics';
import { useMonthRange } from '../hooks/use-month-range';
import {
  determinePrice,
  getNumberParts,
  isFilledArray,
  parseNumber,
} from '../lib';
import {
  ServiceDiscount,
  ServiceSchedule,
  ServiceTimeOption,
} from '../shared-types';
import { DateField } from './date-field';
import { useLocalize } from './localize-context';
import { MessageBtn } from './message-btn';
import { SelectField } from './select-field';
import { TextField } from './text-field';
import { VisitorContext } from './visitor-context';
import { VisitorTypeSelect } from './visitor-type-select';

type ServiceBookingFormProps = {
  productName: string;
  productUid: string;
  adultPrice: number;
  kidPrice: number;
  schedules: null | Array<ServiceSchedule>;
  timeOptions?: Array<ServiceTimeOption>;
  discounts?: Array<ServiceDiscount>;
};

export const ServiceBookingForm = (props: ServiceBookingFormProps) => {
  const localize = useLocalize();
  const [date, setDate] = React.useState('');
  const [time, setTime] = React.useState('');
  const [visitorType] = React.useContext(VisitorContext);
  const [adultCount, setAdultCount] = React.useState('');
  const [kidCount, setKidCount] = React.useState('');
  const adultNum = parseNumber(adultCount);
  const kidNum = parseNumber(kidCount);
  const totalPeople = adultNum + kidNum;
  const [adultPrice, kidPrice] = React.useMemo(
    () =>
      props.discounts
        ? determinePrice(
            props.discounts,
            props.adultPrice,
            props.kidPrice,
            totalPeople
          )
        : [props.adultPrice, props.kidPrice],
    [totalPeople, props.adultPrice, props.kidPrice, props.discounts]
  );

  const [totalPrice, decimals] = getNumberParts(
    adultNum * adultPrice + kidNum * kidPrice
  );
  const allowedDates = React.useMemo(
    () =>
      props.schedules
        ? props.schedules.map(schedule => schedule.date)
        : undefined,
    [props.schedules]
  );
  const selectedSchedule = React.useMemo(
    () =>
      props.schedules && date
        ? props.schedules.find(schedule => schedule.date === date)
        : undefined,
    [date, props.schedules]
  );
  const { currentMonthStartRef, lastMonthDate, currentDateRef } = useMonthRange(
    props.schedules
  );
  const whatsappMsg = `${localize('Service')}: ${props.productName}
${localize('Date')}: ${date} ${time ? `(${time})` : ''}
${localize(visitorType === 'foreigner' ? 'Foreigner' : 'Malaysian')}
${localize('Adult')}: ${adultCount || 0}, ${localize('Kid')}: ${kidCount || 0}
${localize('Total')}: ${totalPrice}.${decimals}
`;

  return (
    <div>
      <div className="max-w-5xl md:flex md:mb-4 md:space-x-2">
        <DateField
          value={date}
          onChangeValue={newDate => {
            setDate(newDate);
            if (props.schedules) {
              const selectedSchedule = props.schedules.find(
                schedule => schedule.date === newDate
              );
              if (selectedSchedule) {
                setTime(selectedSchedule.time[0]);
              }
            }
          }}
          label={localize('Date')}
          allowedDates={allowedDates}
          minDate={currentDateRef.current}
          fromMonth={props.schedules ? currentMonthStartRef.current : undefined}
          toMonth={props.schedules ? lastMonthDate : undefined}
          wrapperClassName="mb-4"
          required
        />
        {selectedSchedule && (
          <SelectField
            value={time}
            onChangeValue={setTime}
            options={selectedSchedule.time}
            label={localize('Time')}
            wrapperClassName="mb-4"
          />
        )}
        {props.timeOptions && isFilledArray(props.timeOptions) && (
          <SelectField
            value={time}
            onChangeValue={setTime}
            options={props.timeOptions.map(option => option.displayTime)}
            label={localize('Time')}
            wrapperClassName="mb-4"
          />
        )}
      </div>
      <div className="max-w-5xl md:flex md:space-x-2">
        <VisitorTypeSelect wrapperClassName="flex-1 mb-4 md:w-48" />
        <div className="space-y-4 xs:flex xs:space-y-0 xs:space-x-2 mb-4">
          <TextField
            type="number"
            step={1}
            min={0}
            value={adultCount}
            onChangeValue={setAdultCount}
            placeholder={localize('pax')}
            append={localize('Adult')}
            className="w-20"
            wrapperClassName="flex-1"
          />
          <TextField
            type="number"
            step={1}
            min={0}
            value={kidCount}
            onChangeValue={setKidCount}
            placeholder={localize('pax')}
            append={localize('Kid')}
            className="w-20"
            wrapperClassName="flex-1"
          />
        </div>
      </div>
      <div
        className={cx(
          'pt-3',
          props.adultPrice && 'xs:flex justify-between items-center'
        )}
      >
        {props.adultPrice && (
          <output className="block mb-2">
            <small className="text-2xl mr-1">{localize('RM')}</small>
            <span className="text-4xl">{totalPrice}</span>
            <small className="text-2xl">.{decimals}</small>
          </output>
        )}
        <div className="text-right">
          <MessageBtn
            message={whatsappMsg}
            onClick={() => {
              trackEvent({
                category: 'button',
                action: 'click',
                label: `order ${props.productUid}`,
                value: totalPrice,
              });
            }}
            disabled={!date || (!adultCount && !kidCount)}
          />
        </div>
      </div>
    </div>
  );
};
